import { inject, Injectable } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { NeoDialog, NeoDialogRef } from '@neogov/pattern-library/neo-dialog';
import { FlyoutV2Size, NeoFlyoutV2 } from '@neogov/pattern-library/neo-flyout-v2';
import { FlyoutOptions, FlyoutSize } from './flyout.types';

/**
 * FlyoutProvider
 *
 * Centralized flyout service for Case Manager.
 * Wraps NeoFlyoutV2 with consistent configuration and simplified API.
 *
 * Features:
 * - Consistent defaults (size: md)
 * - Simplified API reducing boilerplate
 * - Type-safe configuration with generics
 * - Full NeoDialogRef support for afterClosed() subscriptions
 *
 * @example
 * ```typescript
 * import { FlyoutProvider, FlyoutSize } from '@core';
 *
 * export class MyComponent {
 *   private readonly _flyout = inject(FlyoutProvider);
 *
 *   onEdit(item: Item): void {
 *     const flyoutRef = this._flyout.open(EditComponent, {
 *       size: FlyoutSize.md,
 *       data: { item, mode: 'edit' },
 *     });
 *
 *     flyoutRef.afterClosed()
 *       .pipe(takeUntilDestroyed(this._destroyRef))
 *       .subscribe((result) => {
 *         if (result?.action === 'saved') {
 *           this._reload();
 *         }
 *       });
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class FlyoutProvider {
  private readonly _neoFlyout = inject(NeoFlyoutV2);
  private readonly _neoDialog = inject(NeoDialog);

  /**
   * Opens a flyout panel with the specified component and options.
   *
   * The flyout slides in from the right side of the screen and overlays
   * the current content. The backdrop is clickable to close by default.
   *
   * @template TData - The type of data passed to the component
   * @template TResult - The type of result returned when the flyout closes
   * @template TComponent - The type of the component being opened (inferred from component parameter)
   * @param component - The Angular component to display in the flyout
   * @param options - Configuration options (size, data)
   * @returns NeoDialogRef for afterClosed() subscription and manual control
   *
   * @example Basic usage with small size
   * ```typescript
   * this._flyout.open(EditSlaComponent, {
   *   size: FlyoutSize.sm,
   *   data: { configuration: slaConfig },
   * });
   * ```
   *
   * @example Medium size with result handling
   * ```typescript
   * const flyoutRef = this._flyout.open(StatusFlyoutComponent, {
   *   size: FlyoutSize.md,
   *   data: { mode: 'create', categories, existingNames },
   * });
   *
   * flyoutRef.afterClosed().subscribe((result) => {
   *   if (result?.action === 'saved') {
   *     this._reloadGrid();
   *   }
   * });
   * ```
   *
   * @example Large size for content-heavy views
   * ```typescript
   * this._flyout.open(ViewEmailTemplateComponent, {
   *   size: FlyoutSize.lg,
   *   data: { templateId: 123 },
   * });
   * ```
   *
   * @example With generic types for type safety
   * ```typescript
   * interface EditData {
   *   item: Item;
   *   mode: 'create' | 'edit';
   * }
   *
   * interface EditResult {
   *   action: 'saved' | 'cancelled';
   *   item?: Item;
   * }
   *
   * const flyoutRef = this._flyout.open<EditData, EditResult>(
   *   EditComponent,
   *   {
   *     size: FlyoutSize.md,
   *     data: { item: myItem, mode: 'edit' },
   *   }
   * );
   *
   * flyoutRef.afterClosed().subscribe((result: EditResult | undefined) => {
   *   if (result?.action === 'saved') {
   *     console.log('Saved item:', result.item);
   *   }
   * });
   * ```
   */
  public open<TData = unknown, TResult = unknown, TComponent = unknown>(
    component: ComponentType<TComponent>,
    options?: FlyoutOptions<TData>,
  ): NeoDialogRef<TComponent, TResult> {
    // Map FlyoutSize to FlyoutV2Size
    const size = this._mapSize(options?.size);

    const extraClasses = options?.panelClass
      ? Array.isArray(options.panelClass)
        ? options.panelClass
        : [options.panelClass]
      : [];

    if (!extraClasses.length) {
      return this._neoFlyout.open(component, { size, data: options?.data }) as NeoDialogRef<TComponent, TResult>;
    }

    const combinedConfig = {
      ...NeoFlyoutV2.defaultConfig,
      data: options?.data,
      panelClass: ['flyout-panel', `neo-flyout-size-${size}`, ...extraClasses],
      ...(options?.width && { width: options.width }),
      ...(options?.maxWidth && { maxWidth: options.maxWidth }),
    };

    return this._neoDialog.open(component, combinedConfig) as NeoDialogRef<TComponent, TResult>;
  }

  /**
   * Maps FlyoutSize enum to FlyoutV2Size enum.
   * Defaults to 'md' if no size specified.
   *
   * @private
   */
  private _mapSize(size?: FlyoutSize): FlyoutV2Size {
    if (!size) {
      return FlyoutV2Size.md;
    }

    // Direct mapping - enums have same values
    const sizeMap: Record<FlyoutSize, FlyoutV2Size> = {
      [FlyoutSize.sm]: FlyoutV2Size.sm,
      [FlyoutSize.md]: FlyoutV2Size.md,
      [FlyoutSize.lg]: FlyoutV2Size.lg,
      [FlyoutSize.xl]: FlyoutV2Size.xl,
    };

    return sizeMap[size];
  }
}
